import { Check, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type { EnrichmentDisclosureGroup } from './types';

interface AttributeDisclosureCardProps {
  group: EnrichmentDisclosureGroup;
  /** `data-testid` for the card; each field chip gets `${testId}-item-{key}`. */
  testId?: string;
}

/**
 * One read-only attribute group: a heading, a field count, and a row of inert
 * chips. Deliberately NOT `AttributePill` — that renders `role="checkbox"`, and a
 * pre-checked disabled checkbox still reads as a choice. These are plain `<li>`s
 * with a tick glyph, so nothing here is focusable or announces as a control.
 * The agent's behaviour note (and closed enum, when one exists) surfaces on hover.
 *
 * `attribute.alwaysOn` further splits this already-read-only set: a locked, muted
 * chip for fields the agent produces no matter what was requested, versus the
 * ordinary accent tick for fields that reflect this run's actual selection.
 */
export function AttributeDisclosureCard({
  group,
  testId = `attribute-disclosure-card-${group.slug}`,
}: AttributeDisclosureCardProps) {
  const { t } = useTranslation('common');

  return (
    <div data-testid={testId} className="overflow-hidden rounded-lg border">
      <div className="bg-muted/40 flex items-center gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold">{group.label}</span>
          {group.subLabel && (
            <span className="text-muted-foreground text-xs">({group.subLabel})</span>
          )}
        </div>
        <span
          data-testid={`${testId}-field-count`}
          className="text-muted-foreground ml-auto text-xs font-semibold tabular-nums"
        >
          {t('enrichmentRun.disclosure.fieldCount', { count: group.attributes.length })}
        </span>
      </div>
      <ul className="flex flex-wrap gap-2 border-t bg-white px-4 py-3.5" aria-label={group.label}>
        {group.attributes.map((attribute) => {
          const hasNote = attribute.note !== undefined && attribute.note !== '';
          // An always-on field isn't part of what the operator asked for — it's
          // forced regardless — so it gets a lock glyph and muted styling instead
          // of the accent tick, and a tooltip even when the catalogue gave it no
          // behaviour note, so the distinction from an honoured field is never
          // hover-only-by-accident.
          const alwaysOn = attribute.alwaysOn === true;
          const chip = (
            <li
              data-testid={`${testId}-item-${attribute.key}`}
              className={cn(
                'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium',
                alwaysOn
                  ? 'border-border bg-muted/40 text-muted-foreground'
                  : 'border-border text-foreground bg-white',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'grid size-3.5 shrink-0 place-content-center rounded-sm',
                  alwaysOn ? 'bg-muted-foreground/40 text-white' : 'bg-accent-main text-white',
                )}
              >
                {alwaysOn ? <Lock className="size-2.5" /> : <Check className="size-2.5" />}
              </span>
              {attribute.label}
            </li>
          );
          if (!hasNote && !alwaysOn) return <span key={attribute.key}>{chip}</span>;
          return (
            <TooltipProvider key={attribute.key}>
              <Tooltip>
                <TooltipTrigger asChild>{chip}</TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {alwaysOn && <p>{t('enrichmentRun.disclosure.alwaysOnHint')}</p>}
                  {hasNote && <p className={alwaysOn ? 'mt-1' : undefined}>{attribute.note}</p>}
                  {attribute.enumValues && attribute.enumValues.length > 0 && (
                    <p className="mt-1 opacity-80">
                      {t('enrichmentRun.disclosure.enumHint', {
                        values: attribute.enumValues.join(', '),
                      })}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </ul>
    </div>
  );
}
